import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type ThemePreference = "light" | "dark" | "system";

export function useThemePreference() {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load theme preference from database on mount
  useEffect(() => {
    const loadThemePreference = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("theme_preference")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          console.error("Error loading theme preference:", error);
          setIsLoading(false);
          return;
        }

        if (data?.theme_preference) {
          setTheme(data.theme_preference);
        }
      } catch (error) {
        console.error("Error loading theme preference:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadThemePreference();
  }, [user, setTheme]);

  // Save theme preference to database
  const saveThemePreference = async (newTheme: ThemePreference) => {
    setTheme(newTheme);

    if (!user) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ theme_preference: newTheme })
        .eq("user_id", user.id);

      if (error) {
        console.error("Error saving theme preference:", error);
      }
    } catch (error) {
      console.error("Error saving theme preference:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    theme: theme as ThemePreference,
    setTheme: saveThemePreference,
    isLoading,
    isSaving,
  };
}
